import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../shared/contact-us/contact-us.component';

/**
 * Single entry point for the "Contact Us" lead-capture form.
 *
 * The navbar, footer, home, every service page and every industry page each
 * carried their own copy of this `modalService.open(...)` call, which is how
 * the blog, product and case-study CTAs ended up falling back to `mailto:`
 * instead. Anything that needs the form now calls this service, so the modal
 * options (and therefore its look) can only ever be defined in one place.
 *
 * Guarded to the browser: the modal must never be constructed during
 * prerender/SSR, where there is no document to attach it to.
 */
@Injectable({ providedIn: 'root' })
export class ContactModalService {

  constructor(
    private modalService: NgbModal,
    @Inject(PLATFORM_ID) private platformId: object
  ) { }

  /**
   * Opens the Contact Us form. Returns the modal ref in the browser and
   * `null` on the server, so callers can stay `void` without a platform check.
   */
  open(): NgbModalRef | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    return this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      scrollable: true,
      windowClass: 'clean-modal-ui'
    });
  }

  /**
   * True when a data-authored CTA href means "talk to us" rather than "go
   * here" -- i.e. a `mailto:` to any Kalpita inbox.
   *
   * The blog, product and case-study content models all express their primary
   * CTA as an href string. Rather than migrating 33 data files to a new
   * `action` field, the shared templates ask this predicate whether to render
   * a link or a button that opens the form. Any future `mailto:` CTA is
   * therefore wired to the form automatically.
   */
  isContactCta(href: string | null | undefined): boolean {
    return !!href && href.trim().toLowerCase().startsWith('mailto:');
  }
}
